import React, { useEffect, useState } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import { CircularProgress } from "@mui/material";
import axiosInstance from "../../config/axiosConfig";
import Actions from "../../components/courses/Actions";

const customModalStyles = {
  content: {
    padding: "24px",
    width: "90%",
    maxWidth: "700px",
    maxHeight: "90vh",
    margin: "auto",
    borderRadius: "12px",
    boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
    overflowY: "auto",
  },
};

const emptyForm = {
  name: "",
  deptId: "",
  description: "",
  vision: "",
  mission: "",
  facultyId: "",
  existingImages: [],
};

const Department = () => {
  const [departments, setDepartments] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [newImageFiles, setNewImageFiles] = useState([]);
  const [newImagePreviews, setNewImagePreviews] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);

  useEffect(() => {
    fetchFaculties();
    fetchDepartments();
  }, []);

  const fetchFaculties = async () => {
    try {
      const response = await axiosInstance.get("faculty/all");
      setFaculties(response.data.data || []);
    } catch (error) {
      console.error("Error fetching faculties:", error);
    }
  };

  const fetchDepartments = async () => {
    setFetchLoading(true);
    try {
      const response = await axiosInstance.get("department/all");
      setDepartments(response.data.data || []);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
    setFetchLoading(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const currentTotal = (formData.existingImages?.length || 0) + newImageFiles.length;
    const remainingSlots = 10 - currentTotal;

    if (remainingSlots <= 0) {
      Swal.fire("Limit reached", "You can upload a maximum of 10 images per department.", "warning");
      e.target.value = "";
      return;
    }

    const filesToAdd = files.slice(0, remainingSlots);
    if (files.length > remainingSlots) {
      Swal.fire("Note", `Only ${remainingSlots} images were added to stay within the 10-image limit.`, "info");
    }

    const newPreviews = filesToAdd.map((file) => URL.createObjectURL(file));
    setNewImageFiles((prev) => [...prev, ...filesToAdd]);
    setNewImagePreviews((prev) => [...prev, ...newPreviews]);
    e.target.value = "";
  };

  const handleRemoveExistingImage = (indexToRemove) => {
    setFormData((prev) => ({
      ...prev,
      existingImages: prev.existingImages.filter((_, idx) => idx !== indexToRemove),
    }));
  };

  const handleRemoveNewImage = (indexToRemove) => {
    setNewImageFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
    setNewImagePreviews((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const buildFormData = () => {
    const dataToSend = new FormData();
    dataToSend.append("name", formData.name || "");
    dataToSend.append("deptId", formData.deptId || "");
    dataToSend.append("facultyId", formData.facultyId || "");
    dataToSend.append("description", formData.description || "");
    dataToSend.append("vision", formData.vision || "");
    dataToSend.append("mission", formData.mission || "");

    dataToSend.append(
      "existingImages",
      JSON.stringify(formData.existingImages || [])
    );

    newImageFiles.forEach((file) => {
      dataToSend.append("images[]", file);
    });

    return dataToSend;
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setButtonLoading(true);
    try {
      const dataToSend = buildFormData();
      await axiosInstance.post("department/create", dataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchDepartments();
      setShowCreateModal(false);
      setFormData(emptyForm);
      setNewImageFiles([]);
      setNewImagePreviews([]);
      Swal.fire("Success!", "Department created successfully!", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to create department",
        "error"
      );
    }
    setButtonLoading(false);
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setButtonLoading(true);
    try {
      const dataToSend = buildFormData();
      dataToSend.append("_method", "PUT");
      await axiosInstance.post(`department/change/${editId}`, dataToSend, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await fetchDepartments();
      setShowEditModal(false);
      setFormData(emptyForm);
      setNewImageFiles([]);
      setNewImagePreviews([]);
      Swal.fire("Success!", "Department updated successfully!", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to update department",
        "error"
      );
    }
    setButtonLoading(false);
  };

  const handleView = async (id) => {
    try {
      setNewImageFiles([]);
      setNewImagePreviews([]);
      setShowEditModal(true);
      setEditId(id);
      const response = await axiosInstance.get(`department/single/${id}`);
      const data = response?.data?.data;

      const imgs = Array.isArray(data?.images)
        ? data.images
        : (data?.img ? [data.img] : []);

      setFormData({
        name: data?.name || "",
        deptId: data?.deptId || data?.dept_id || "",
        description: data?.description || "",
        vision: data?.vision || "",
        mission: data?.mission || "",
        facultyId: data?.facultyId ? String(data.facultyId) : "",
        existingImages: imgs,
      });
    } catch (error) {
      Swal.fire("Error", "Failed to load department", "error");
    }
  };

  const handleDelete = async (id) => {
    try {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Yes, delete it!",
      });
      if (!result.isConfirmed) return;

      await axiosInstance.delete(`department/delete/${id}`);
      await fetchDepartments();
      Swal.fire("Deleted!", "Department deleted successfully", "success");
    } catch (error) {
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to delete department",
        "error"
      );
    }
  };

  const totalImageCount = (formData.existingImages?.length || 0) + newImageFiles.length;

  const renderForm = (onSubmit, submitLabel) => (
    <form onSubmit={onSubmit}>
      <div style={{ marginBottom: "12px" }}>
        <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
          Department Name *
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          style={{
            width: "100%",
            padding: "8px 12px",
            fontSize: "14px",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
        <div>
          <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
            Department ID / Code
          </label>
          <input
            type="text"
            name="deptId"
            placeholder="e.g. dept-cs"
            value={formData.deptId}
            onChange={handleChange}
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
            Faculty *
          </label>
          <select
            name="facultyId"
            value={formData.facultyId}
            onChange={handleChange}
            required
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          >
            <option value="">Select faculty</option>
            {faculties.map((faculty) => (
              <option key={faculty._id} value={faculty._id}>
                {faculty.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: "12px" }}>
        <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
          Description / Overview
        </label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={3}
          style={{
            width: "100%",
            padding: "8px 12px",
            fontSize: "14px",
            borderRadius: "6px",
            border: "1px solid #ccc",
          }}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
        <div>
          <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
            Department Vision
          </label>
          <textarea
            name="vision"
            placeholder="To become a leading department..."
            value={formData.vision}
            onChange={handleChange}
            rows={3}
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
        </div>

        <div>
          <label style={{ display: "block", fontWeight: "bold", marginBottom: "5px" }}>
            Department Mission
          </label>
          <textarea
            name="mission"
            placeholder="To produce future thinkers..."
            value={formData.mission}
            onChange={handleChange}
            rows={3}
            style={{
              width: "100%",
              padding: "8px 12px",
              fontSize: "14px",
              borderRadius: "6px",
              border: "1px solid #ccc",
            }}
          />
        </div>
      </div>

      {/* MULTIPLE DEPARTMENT IMAGES (UP TO 10) */}
      <div style={{ marginBottom: "16px", padding: "12px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
          <label style={{ fontWeight: "bold", fontSize: "14px" }}>
            Department Top Banner Images ({totalImageCount} / 10)
          </label>
          <span style={{ fontSize: "12px", color: totalImageCount >= 10 ? "#dc2626" : "#0284c7" }}>
            {totalImageCount >= 10 ? "Maximum limit reached" : `Can add up to ${10 - totalImageCount} more`}
          </span>
        </div>

        {/* Existing Images Thumbnails */}
        {formData.existingImages?.length > 0 && (
          <div style={{ marginBottom: "10px" }}>
            <span style={{ fontSize: "12px", fontWeight: "600", color: "#64748b", display: "block", marginBottom: "6px" }}>
              Current Images:
            </span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {formData.existingImages.map((imgUrl, idx) => (
                <div
                  key={idx}
                  style={{
                    position: "relative",
                    width: "80px",
                    height: "60px",
                    borderRadius: "6px",
                    overflow: "hidden",
                    border: "2px solid #006699",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <img
                    src={imgUrl}
                    alt={`Department banner ${idx + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveExistingImage(idx)}
                    style={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      background: "rgba(220, 38, 38, 0.9)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                    title="Remove Image"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* New Images Previews */}
        {newImagePreviews.length > 0 && (
          <div style={{ marginBottom: "10px" }}>
            <span style={{ fontSize: "12px", fontWeight: "600", color: "#16a34a", display: "block", marginBottom: "6px" }}>
              Newly Added Images (will be saved):
            </span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {newImagePreviews.map((previewUrl, idx) => (
                <div
                  key={idx}
                  style={{
                    position: "relative",
                    width: "80px",
                    height: "60px",
                    borderRadius: "6px",
                    overflow: "hidden",
                    border: "2px solid #16a34a",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  <img
                    src={previewUrl}
                    alt={`New upload ${idx + 1}`}
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveNewImage(idx)}
                    style={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      background: "rgba(220, 38, 38, 0.9)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: "18px",
                      height: "18px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                    title="Remove Image"
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* File Picker input */}
        {totalImageCount < 10 && (
          <div>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              style={{
                width: "100%",
                padding: "8px",
                fontSize: "13px",
                borderRadius: "6px",
                border: "1px dashed #cbd5e1",
                background: "#fff",
                cursor: "pointer",
              }}
            />
            <span style={{ fontSize: "11px", color: "#64748b", marginTop: "4px", display: "block" }}>
              Upload up to 10 images of department labs, classrooms, student activities, or events.
            </span>
          </div>
        )}
      </div>

      <button
        type="submit"
        disabled={buttonLoading}
        style={{
          padding: "10px 24px",
          backgroundColor: "#006699",
          color: "#fff",
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
          fontWeight: "bold",
          fontSize: "14px",
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {buttonLoading ? <CircularProgress size={20} color="inherit" /> : submitLabel}
      </button>
    </form>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <button
          onClick={() => {
            setFormData(emptyForm);
            setNewImageFiles([]);
            setNewImagePreviews([]);
            setShowCreateModal(true);
          }}
          style={{
            padding: "10px 20px",
            backgroundColor: "#006699",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
          }}
        >
          + Create New Department
        </button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "20px" }}>
        {fetchLoading ? (
          <div
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 9999,
            }}
          >
            <CircularProgress />
          </div>
        ) : (
          departments.map((department) => {
            const imgCount = Array.isArray(department.images)
              ? department.images.length
              : (department.img ? 1 : 0);
            const thumb = Array.isArray(department.images) && department.images[0]
              ? department.images[0]
              : department.img;

            return (
              <div
                key={department._id}
                style={{
                  width: "300px",
                  borderRadius: "10px",
                  backgroundColor: "#fff",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                {thumb ? (
                  <div style={{ height: "140px", width: "100%", position: "relative", overflow: "hidden" }}>
                    <img
                      src={thumb}
                      alt={department.name}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                    <span
                      style={{
                        position: "absolute",
                        bottom: "8px",
                        right: "8px",
                        background: "rgba(0,0,0,0.7)",
                        color: "#fff",
                        fontSize: "11px",
                        padding: "2px 8px",
                        borderRadius: "12px",
                        fontWeight: "600",
                      }}
                    >
                      📸 {imgCount} {imgCount === 1 ? "Image" : "Images"}
                    </span>
                  </div>
                ) : (
                  <div
                    style={{
                      height: "80px",
                      background: "linear-gradient(135deg, #006699, #004e75)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#fff",
                      fontSize: "13px",
                      fontWeight: "bold",
                    }}
                  >
                    No Banner Images
                  </div>
                )}

                <div style={{ padding: "16px" }}>
                  <h3 style={{ margin: "0 0 6px", fontSize: "16px", fontWeight: "bold" }}>
                    {department.name}
                  </h3>
                  <p style={{ margin: "0 0 8px", fontSize: "12px", color: "#006699", fontWeight: "600" }}>
                    {department.facultyName || "No faculty assigned"}
                  </p>
                  {department.vision && (
                    <p style={{ margin: "0 0 8px", fontSize: "12px", color: "#475569", fontStyle: "italic", maxHeight: "40px", overflow: "hidden" }}>
                      <strong>Vision:</strong> {department.vision}
                    </p>
                  )}
                  <p
                    style={{
                      color: "#64748b",
                      fontSize: "13px",
                      maxHeight: "50px",
                      overflow: "hidden",
                      margin: "0 0 12px",
                    }}
                  >
                    {department.description || "No description"}
                  </p>
                  <Actions
                    courseId={department._id}
                    onEdit={handleView}
                    onDelete={handleDelete}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>

      <Modal
        isOpen={showCreateModal}
        onRequestClose={() => {
          setShowCreateModal(false);
          setNewImageFiles([]);
          setNewImagePreviews([]);
        }}
        style={customModalStyles}
        contentLabel="Create Department"
      >
        <h2 style={{ margin: "0 0 16px", fontSize: "20px", fontWeight: "bold", color: "#0f172a" }}>
          Create New Department
        </h2>
        {renderForm(handleCreate, "Create Department")}
      </Modal>

      <Modal
        isOpen={showEditModal}
        onRequestClose={() => {
          setShowEditModal(false);
          setFormData(emptyForm);
          setNewImageFiles([]);
          setNewImagePreviews([]);
        }}
        style={customModalStyles}
        contentLabel="Edit Department"
      >
        <h2 style={{ margin: "0 0 16px", fontSize: "20px", fontWeight: "bold", color: "#0f172a" }}>
          Edit Department
        </h2>
        {renderForm(handleEdit, "Update Department")}
      </Modal>
    </div>
  );
};

export default Department;
